import type { Address } from 'viem';

import { config } from '../config.ts';
import { reportError } from './errors.ts';

/** User frames carry `user`: a frame of a wallet the app has since left is dropped */
export type FeedMessage = { type: 'market' | 'wallet' | 'account' | 'intents' | 'transfers'; user?: string; data: unknown };
/** `stale`: the socket looks open but nothing came through for a while; it is replaced */
export type FeedStatus = 'connecting' | 'open' | 'stale' | 'closed';

/** The API pushes the market every block, about one a second: this much silence means a dead socket */
const STALE_MS = 20_000;
const WATCH_MS = 5_000;

/**
 * The app's only live data source: one WebSocket to the API, which pushes the market snapshot every
 * block and this user's data whenever it changes. Reconnects with backoff and re-subscribes. `open` means
 * data flows, not just a handshake, and a socket that goes silent is closed and reopened: a laptop
 * waking up or a network change can lose the connection without any close event.
 */
export class Feed {
  private ws: WebSocket | undefined;
  private user: Address | undefined;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly watchdog: ReturnType<typeof setInterval>;
  private lastFrameAt = 0;
  private closed = false;
  private readonly onMessage: (m: FeedMessage) => void;
  private readonly onStatus: (s: FeedStatus) => void;
  /** Failures already logged, by frame type: one line each, not one per block */
  private readonly logged = new Set<string>();

  constructor(onMessage: (m: FeedMessage) => void, onStatus: (s: FeedStatus) => void) {
    this.onMessage = onMessage;
    this.onStatus = onStatus;
    this.connect();
    this.watchdog = setInterval(() => this.check(), WATCH_MS);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') this.check();
  };

  /** Closes a socket that has gone silent; `onclose` then reconnects */
  private check(): void {
    const ws = this.ws;
    if (this.closed || !ws || ws.readyState !== WebSocket.OPEN) return;
    if (Date.now() - this.lastFrameAt < STALE_MS) return;
    this.onStatus('stale');
    ws.close();
  }

  private connect(): void {
    if (this.closed) return;
    this.onStatus('connecting');
    const ws = new WebSocket(config.wsUrl);
    this.ws = ws;
    let first = true;
    ws.onopen = () => {
      this.retry = 0;
      this.lastFrameAt = Date.now();
      if (this.user) ws.send(JSON.stringify({ op: 'subscribe', user: this.user }));
    };
    ws.onmessage = (e) => {
      this.lastFrameAt = Date.now();
      if (first) {
        first = false;
        this.onStatus('open');
      }
      let m: FeedMessage;
      try {
        m = JSON.parse(String(e.data)) as FeedMessage;
      } catch (err) {
        this.log('frame', err);
        return;
      }
      // a frame the app cannot digest is dropped; the next push replaces it, and the console says why
      try {
        this.onMessage(m);
      } catch (err) {
        this.log(m.type, err);
      }
    };
    ws.onclose = () => {
      this.onStatus('closed');
      if (this.closed) return;
      // 0.5 s, 1 s, 2 s ... capped at 15 s, with jitter so a restarted gateway is not stampeded
      const delay = Math.min(15_000, 500 * 2 ** this.retry++) * (0.75 + Math.random() / 2);
      this.timer = setTimeout(() => this.connect(), delay);
    };
  }

  private log(what: string, err: unknown): void {
    if (this.logged.has(what)) return;
    this.logged.add(what);
    reportError(`feed ${what}`, err);
  }

  subscribe(user: Address | undefined): void {
    this.user = user;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(user ? { op: 'subscribe', user } : { op: 'unsubscribe' }));
  }

  close(): void {
    this.closed = true;
    clearTimeout(this.timer);
    clearInterval(this.watchdog);
    document.removeEventListener('visibilitychange', this.onVisible);
    this.ws?.close();
  }
}
